import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Testimonials from "@/components/Testimonials";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const CATEGORY_TILES = [
  { to: "/shop/sofa", title: "Sofa", copy: "Sofas, sofa-cum-beds, center tables.", img: "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c" },
  { to: "/shop/bedroom", title: "Bedroom", copy: "Beds, wardrobes, dressing units.", img: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85" },
  { to: "/shop/living-room", title: "Living Room", copy: "Coffee tables, TV units, shelves.", img: "https://images.unsplash.com/photo-1758977404039-6e834be8eca8" },
  { to: "/shop/dining", title: "Dining", copy: "4 & 6-seater dining sets.", img: "https://images.unsplash.com/photo-1633505899118-4ca6bd143043" },
  { to: "/shop/storage", title: "Storage", copy: "Sideboards, cabinets, shelves.", img: "https://images.unsplash.com/photo-1594620302200-9a762244a156" },
  { to: "/shop/study-office", title: "Study & Office", copy: "Study desks, office tables, chairs.", img: "https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd" },
  { to: "/shop/bar", title: "Bar Furniture", copy: "Cabinets, chairs & bar trolleys.", img: "https://images.unsplash.com/photo-1497219055242-93359eeed651" },
];

function FeaturedCarousel({ items }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (!ref.current) return;
    const w = ref.current.clientWidth;
    ref.current.scrollBy({ left: dir * w * 0.8, behavior: "smooth" });
  };
  if (!items || items.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="eyebrow">This Season's Pick</p>
          <h2 className="font-serif text-4xl md:text-5xl mt-2 tracking-tight">Featured Pieces</h2>
        </div>
        <div className="hidden md:flex gap-2">
          <button
            data-testid="featured-scroll-left"
            aria-label="Scroll featured left"
            onClick={() => scroll(-1)}
            className="p-3 border border-stone-300 hover:border-stone-900 hover:bg-stone-900 hover:text-white transition-colors"
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <button
            data-testid="featured-scroll-right"
            aria-label="Scroll featured right"
            onClick={() => scroll(1)}
            className="p-3 border border-stone-300 hover:border-stone-900 hover:bg-stone-900 hover:text-white transition-colors"
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <div
        ref={ref}
        data-testid="featured-carousel"
        className="flex gap-8 overflow-x-auto snap-x snap-mandatory pb-6"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((p) => (
          <div key={p.id} className="snap-start shrink-0 w-[280px] sm:w-[320px]">
            <ProductCard p={p} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [firstOrderPct, setFirstOrderPct] = useState(5);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 12)))
      .catch(() => setFeatured([]));
    api.get("/payment/config")
      .then((r) => { if (typeof r.data.first_order_discount_pct === "number") setFirstOrderPct(r.data.first_order_discount_pct); })
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden grain">
        <img src="https://images.unsplash.com/photo-1633505899118-4ca6bd143043?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwbGl2aW5nJTIwcm9vbSUyMHdvb2R8ZW58MHx8fHwxNzgyMDg4MzQ3fDA&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/70 via-[#1C1917]/10 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 w-full text-white animate-fade-up">
          <p className="eyebrow text-[#E7D3B3]">Est. Legacy · Made in India</p>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight max-w-3xl mt-4">Furniture with the patience of a lifetime.</h1>
          <p className="mt-6 max-w-xl text-stone-200 text-lg leading-relaxed">Sofas. Beds. Dining. Storage. Every piece we make is joined by hand, finished by feel, and built to outlive trends.</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/shop/sofa" data-testid="hero-cta-shop" className="btn-primary bg-white text-[#1C1917] hover:bg-stone-100">Shop the Collection</Link>
            <Link to="/custom-order" data-testid="hero-cta-custom" className="btn-secondary border-white text-white hover:bg-white hover:text-[#1C1917]">Commission a Piece</Link>
          </div>
        </div>
      </section>

      {/* WELCOME SALE INTRO — matches the reference video */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Left — lifestyle promo (spans 2 cols on desktop) */}
          <div className="md:col-span-2 relative overflow-hidden aspect-[16/10] md:aspect-auto md:min-h-[420px] bg-[#1C1917]">
            <img
              src="https://images.unsplash.com/photo-1633505899118-4ca6bd143043?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxmYW1pbHklMjBkaW5pbmclMjB0YWJsZSUyMHdvb2R8ZW58MHx8fHwxNzgyMDkzMTI4fDA&ixlib=rb-4.1.0&q=85"
              alt="Family dining on Sheesham dining table"
              className="absolute inset-0 w-full h-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1C1917]/80 via-[#1C1917]/40 to-transparent"/>
            <div className="relative z-10 h-full flex flex-col justify-center p-8 md:p-12 lg:p-16 text-white max-w-lg">
              <span data-testid="welcome-badge" className="inline-block self-start text-[10px] tracking-[0.3em] uppercase text-[#E7D3B3] border border-[#E7D3B3]/40 px-3 py-1">Limited · New Customers</span>
              <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 tracking-tight leading-[0.95]">WELCOME<br/>SALE</h2>
              <p className="mt-4 text-2xl md:text-3xl font-serif text-[#E7D3B3]">{firstOrderPct}% OFF on First Order</p>
              <p className="mt-3 text-sm text-stone-200 leading-relaxed">Sign up and your first order comes with an automatic {firstOrderPct}% welcome discount. Premium solid-wood furniture, handcrafted in Bathinda since a lineage of woodworkers.</p>
              <div className="mt-6 flex gap-3 flex-wrap">
                <Link data-testid="welcome-shop-cta" to="/shop/sofa" className="btn-primary bg-white text-[#1C1917] hover:bg-stone-100">Shop Now</Link>
                <Link data-testid="welcome-signup-cta" to="/login" className="btn-secondary border-white text-white hover:bg-white hover:text-[#1C1917]">Sign Up</Link>
              </div>
            </div>
          </div>

          {/* Right — vertical column of category highlights */}
          <div className="flex flex-col gap-6">
            <Link to="/shop/dining" data-testid="welcome-tile-dining" className="group relative block aspect-[4/3] md:aspect-auto md:flex-1 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1758977404039-6e834be8eca8" alt="Dining" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"/>
              <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#E7D3B3]">Family Favourites</p>
                <p className="font-serif text-2xl mt-1">Dining Sets</p>
              </div>
            </Link>
            <Link to="/shop/storage" data-testid="welcome-tile-storage" className="group relative block aspect-[4/3] md:aspect-auto md:flex-1 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1594620302200-9a762244a156" alt="Storage" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"/>
              <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#E7D3B3]">Crafted Storage</p>
                <p className="font-serif text-2xl mt-1">Cabinets & Wardrobes</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="eyebrow">Room by Room</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-2 tracking-tight">Collections</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORY_TILES.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`category-card-${c.to.split("/").pop()}`}
              className="group relative block aspect-[4/3] overflow-hidden"
            >
              <img src={c.img} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent"/>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="font-serif text-2xl md:text-3xl">{c.title}</h3>
                <p className="mt-1 text-stone-200 text-sm">{c.copy}</p>
                <span className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase">Explore <ArrowRight size={12}/></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS CAROUSEL */}
      <FeaturedCarousel items={featured} />

      {/* CUSTOM ORDER CTA */}
      <section className="mt-16 bg-[#1C1917] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow text-[#E7D3B3]">Bespoke · One-of-One</p>
            <h2 className="font-serif text-4xl md:text-6xl mt-3 tracking-tight leading-tight">Have something in mind? We'll make it.</h2>
            <p className="mt-6 text-stone-300 max-w-lg leading-relaxed">Send us your sketch, a photograph, or just an idea. Our karigars will bring it to life in the wood of your choice — from Sheesham to Burma Teak.</p>
            <Link to="/custom-order" data-testid="home-custom-cta" className="mt-8 inline-block btn-accent">Start a Commission</Link>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1497219055242-93359eeed651" alt="" className="w-full h-[500px] object-cover"/>
          </div>
        </div>
      </section>

      {/* CUSTOMER TESTIMONIALS */}
      <Testimonials />

      {/* CRAFTSMANSHIP */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-5">
          <img src="https://images.unsplash.com/photo-1611021061285-16c871740efa" alt="" className="w-full aspect-[4/5] object-cover"/>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <p className="eyebrow">Our Process</p>
          <h2 className="font-serif text-4xl md:text-5xl mt-2 tracking-tight">Slow wood, honest joinery.</h2>
          <p className="mt-6 text-stone-600 text-lg leading-relaxed">We season our timber for months, cut with hand-tools where machines lack soul, and finish with natural oils that let the grain breathe. What you take home is not a product — it is a decade of work distilled into an object.</p>
          <Link to="/about" className="mt-8 inline-block btn-secondary">Meet the Workshop</Link>
        </div>
      </section>
    </div>
  );
}
